import { computed, readonly, ref } from "vue";
import { getAccessToken } from "@wkt/lib/auth-context";
import {
  getKeycloakProfile,
  initKeycloak,
  isKeycloakAuthenticated,
  login,
  logout,
  refreshToken,
  scheduleTokenRefresh,
  stopTokenRefresh,
} from "@wkt/lib/keycloak";

const authenticated = ref(isKeycloakAuthenticated());
const error = ref<string | null>(null);
const initializing = ref(false);
const profile = ref(getKeycloakProfile());

function syncRuntimeSnapshot(): void {
  authenticated.value = isKeycloakAuthenticated();
  profile.value = getKeycloakProfile();
}

export function useKeycloak() {
  async function init(options?: {
    checkLoginIframe?: boolean;
    onLoad?: "check-sso" | "login-required";
    silentCheckSsoFallback?: boolean;
    silentCheckSsoRedirectUri?: string;
  }): Promise<boolean> {
    initializing.value = true;
    error.value = null;

    try {
      const isAuthenticated = await initKeycloak(options);
      syncRuntimeSnapshot();
      return isAuthenticated;
    } catch (currentError) {
      error.value =
        currentError instanceof Error
          ? currentError.message
          : "Unable to initialize Keycloak";
      syncRuntimeSnapshot();
      return false;
    } finally {
      initializing.value = false;
    }
  }

  async function startLogin(
    options?: Record<string, unknown>,
  ): Promise<boolean> {
    error.value = null;

    try {
      const result = await login(options);
      syncRuntimeSnapshot();
      return result;
    } catch (currentError) {
      error.value =
        currentError instanceof Error
          ? currentError.message
          : "Unable to login with Keycloak";
      syncRuntimeSnapshot();
      return false;
    }
  }

  async function refresh(minValidity = 10): Promise<boolean> {
    error.value = null;

    try {
      const refreshed = await refreshToken(minValidity);
      syncRuntimeSnapshot();
      return refreshed;
    } catch (currentError) {
      error.value =
        currentError instanceof Error
          ? currentError.message
          : "Unable to refresh Keycloak token";
      syncRuntimeSnapshot();
      return false;
    }
  }

  async function closeSession(redirectUri?: string): Promise<void> {
    error.value = null;

    try {
      await logout(redirectUri);
    } catch (currentError) {
      error.value =
        currentError instanceof Error
          ? currentError.message
          : "Unable to logout from Keycloak";
    } finally {
      syncRuntimeSnapshot();
    }
  }

  return {
    authenticated: readonly(authenticated),
    error: readonly(error),
    init,
    initializing: readonly(initializing),
    login: startLogin,
    logout: closeSession,
    profile: readonly(profile),
    refreshToken: refresh,
    scheduleTokenRefresh,
    stopTokenRefresh,
    syncFromRuntime: syncRuntimeSnapshot,
    token: computed(() => getAccessToken()),
  };
}
